import React from 'react';
import styles from './JurorVoteCountBanner.module.scss';
import {
    useJuryStore,
    useJuryFundingOptions,
    useJuryPointsConfiguration,
    useJuryVotingType,
} from '@core/frontend/stores';
import JuryBadge from '../JuryBadge/JuryBadge';
import { mergeClassNames } from '@core/shared';

/**
 * JurorVoteCountBanner - Sticky header component for Jury views.
 * Shows how many funding slots have been assigned out of the total available,
 * or — in points mode — how many points are still available / have been given.
 */
const JurorVoteCountBanner = ({ isAuthenticated }) => {
    const votingType = useJuryVotingType();
    const pointsConfig = useJuryPointsConfiguration();
    const poll = useJuryStore((s) => s.poll);
    const points = useJuryStore((s) => s.points);
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const fundingOptions = useJuryFundingOptions();

    const isPointsMode = votingType === 'points';

    const assignedCount = Object.keys(poll).length;
    const totalCount = fundingOptions.length;
    const remainingCount = totalCount - assignedCount;

    const usedPoints = Object.values(points).reduce((sum, value) => sum + value, 0);
    const isPointsLimitEnabled = pointsConfig.isTotalPointsLimitEnabled && pointsConfig.maxTotalPoints !== null;
    const remainingPoints = isPointsLimitEnabled ? Math.max(0, pointsConfig.maxTotalPoints - usedPoints) : null;

    const bannerClasses = mergeClassNames(
        styles.JurorVoteCountBanner,
        isConfirmed ? styles.JurorVoteCountBannerConfirmed : ''
    );

    const textClasses = mergeClassNames(styles.BannerText, isConfirmed ? 'text-white-no-contrast-preference' : '');

    const renderPointsStatus = () => {
        if (isConfirmed) return <>Ihre Punktevergabe wurde erfolgreich bestätigt.</>;
        if (remainingPoints !== null) {
            return (
                <>
                    Noch <JuryBadge>{remainingPoints}</JuryBadge> von {pointsConfig.maxTotalPoints} Punkten verfügbar
                </>
            );
        }
        return (
            <>
                <JuryBadge>{usedPoints}</JuryBadge> Punkte vergeben
            </>
        );
    };

    const renderFundingStatus = () => {
        if (isConfirmed) return <>Ihre Förderungsvergabe wurde erfolgreich bestätigt.</>;
        return (
            <>
                Noch <JuryBadge>{remainingCount}</JuryBadge> von {totalCount} Teilnehmer fördern
            </>
        );
    };

    return (
        <div id="juror-vote-count-banner" className={bannerClasses} role="status" aria-live="polite">
            <div className={textClasses}>
                {isAuthenticated ? (
                    isPointsMode ? (
                        renderPointsStatus()
                    ) : (
                        renderFundingStatus()
                    )
                ) : (
                    <>Bitte melden Sie sich an, um fortzufahren</>
                )}
            </div>
        </div>
    );
};

export default JurorVoteCountBanner;
